import type { ApplicationExecutionSummary } from "./application-execution";
export const STAGES = [
  { id: "potential", label: "Potential" },
  { id: "priority", label: "Priority" },
  { id: "applied_contacted", label: "Applied / Contacted" },
  { id: "backburner", label: "Backburner" },
  { id: "actively_engaging", label: "Actively Engaging" },
  { id: "early_interviews", label: "Early Interviews" },
  { id: "late_interviews", label: "Late Interviews" },
  { id: "negotiations", label: "Negotiations" },
  { id: "secured", label: "Secured" },
  { id: "killed", label: "Killed" },
  { id: "archive", label: "Archive" },
] as const;

export type Stage = (typeof STAGES)[number]["id"];
export type CardKind = "job" | "person";

export interface Task {
  id: string;
  cardId: string;
  title: string;
  dueOn: string | null;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface Activity {
  id: string;
  cardId: string;
  eventType: string;
  body: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
}

export interface BoardCard {
  applicationExecution?: ApplicationExecutionSummary | null;
  id: string;
  kind: CardKind;
  primaryName: string;
  company: string;
  sourceUrl: string | null;
  stage: Stage;
  position: number;
  earliestTask: Pick<Task, "id" | "title" | "dueOn"> | null;
  createdAt: string;
  updatedAt: string;
}

export interface CardDetail extends Omit<BoardCard, "earliestTask"> {
  description: string;
  canonicalKey: string | null;
  legacyJobId: string | null;
  contactEmail: string | null;
  summaryBullets: string[];
  summaryGeneratedAt: string | null;
  summaryModel: string | null;
  companyLinkedInUrl: string | null;
  companyLinkedInResolvedAt: string | null;
  companyLinkedInModel: string | null;
  tasks: Task[];
  activities: Activity[];
}

export type CompanyJobsLinkStatus = "verified" | "fallback";

export interface CompanyJobsLinkResponse {
  card: CardDetail;
  jobsUrl: string;
  status: CompanyJobsLinkStatus;
}

export interface GlobalTask extends Task {
  card: {
    id: string;
    kind: CardKind;
    primaryName: string;
    company: string;
    sourceUrl: string | null;
    stage: Stage;
  };
}

export interface GlobalTasksResponse {
  tasks: GlobalTask[];
  serverTime: string;
}

export interface BoardPage {
  cards: BoardCard[];
  nextCursor: string | null;
  total: number;
}

export interface BoardResponse {
  pages: Partial<Record<Stage, BoardPage>>;
  stageCounts: Record<Stage, number>;
  stages: typeof STAGES;
  syncCursor: string;
  serverTime: string;
}

export interface BoardChangesResponse {
  cards: BoardCard[];
  stageCounts: Record<Stage, number>;
  syncCursor: string;
  serverTime: string;
}

export interface ApiError {
  error: { code: string; message: string };
}

export type LinkedInIngestionStatus =
  | "fetched"
  | "partial"
  | "unavailable"
  | "not_applicable";

export interface LinkedInIngestionResult {
  status: LinkedInIngestionStatus;
  fields: Array<"title" | "company" | "description">;
}

export interface LinkedInCardResponse {
  card: CardDetail;
  duplicate: boolean;
  ingestion: LinkedInIngestionResult;
}

export function isStage(value: string): value is Stage {
  return STAGES.some((stage) => stage.id === value);
}
